// src/services/firestoreService.js
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getCountFromServer,
  serverTimestamp,
  addDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import app from "./firebase";

export const db = getFirestore(app);

// ── User profile ───────────────────────────────────────────────────────────────
// pendingRole: role chosen at registration — only used when creating a NEW profile.
export const createUserProfile = async (user, pendingRole = null) => {
  const ref  = doc(db, "users", user.uid);
  const snap = await getDoc(ref);

  // Profile already exists — return it as-is (don't overwrite role)
  if (snap.exists()) return snap.data();

  const role = pendingRole || "freelancer";

  const profile = {
    uid:         user.uid,
    displayName: user.displayName || "",
    email:       user.email,
    photoURL:    user.photoURL    || "",
    role,
    credits:     5,
    skills:      [],
    bio:         "",
    location:    "",
    createdAt:   serverTimestamp(),
    updatedAt:   serverTimestamp(),
  };

  await setDoc(ref, profile);
  return profile;
};

export const getUserProfile = async (uid) => {
  const ref  = doc(db, "users", uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
};

export const updateUserProfile = async (uid, data) => {
  const ref = doc(db, "users", uid);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
};

// Words that are unambiguously non-English and commonly appear in job titles
// scraped from SA classifieds (Indonesian, French, Portuguese, Arabic romanised)
const NON_ENGLISH_TITLE_RE = /\b(penulis|konten|untuk|dengan|yang|kreatif|kami|adalah|bisa|akan|juga|dari|dalam|anda|atau|serta|bahwa|kita|oleh|lebih|nous|faisons|votre|notre|créations|leur|leurs|vous\b|pelo|pela|nossa|nosso|você|também|somos|hacemos|nuestro|nosotros|estamos|mira|شراكات|إشراف|تشطيبات|بالسعودية|مطلوب|وظيفة|عمل)\b/i;

const isEnglishJob = (job) => {
  const title = (job.title || "").trim();
  if (!title) return false;
  // Drop non-Latin scripts (Arabic, Chinese, etc.)
  const latinChars = (title.match(/[a-zA-Z0-9\s.,!?()\-'"]/g) || []).length;
  if (latinChars / title.length <= 0.55) return false;
  // Drop Latin-script non-English (Indonesian, French, Portuguese, etc.)
  if (NON_ENGLISH_TITLE_RE.test(title)) return false;
  return true;
};

// ── Jobs ───────────────────────────────────────────────────────────────────────
// NOTE: The query uses only `where("approved")` + `limit` to avoid requiring
// a composite Firestore index. Client-side sort by datePosted handles ordering.
export const getLocalJobs = async ({ approved = true, maxLimit = 50, source = null } = {}) => {
  try {
    const constraints = [
      where("approved", "==", approved),
      limit(maxLimit),
    ];
    if (source) constraints.unshift(where("source", "==", source));

    const q    = query(collection(db, "jobs"), ...constraints);
    const snap = await getDocs(q);
    const jobs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(isEnglishJob);

    // Client-side sort — newest first
    return jobs.sort((a, b) => {
      const ta = a.datePosted?.toDate?.() || new Date(a.datePosted || 0);
      const tb = b.datePosted?.toDate?.() || new Date(b.datePosted || 0);
      return tb - ta;
    });
  } catch (err) {
    console.warn("getLocalJobs failed:", err.message);
    return [];
  }
};

// Efficient total count — no documents fetched, just a server-side count
export const getApprovedJobCount = async () => {
  try {
    const q    = query(collection(db, "jobs"), where("approved", "==", true));
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch {
    return null;
  }
};

// All pending jobs (not yet approved) — for admin review
export const getPendingJobs = async () =>
  getLocalJobs({ approved: false, maxLimit: 100, source: null });

export const approveJob = async (jobId, approved = true) => {
  const ref = doc(db, "jobs", jobId);
  await updateDoc(ref, {
    approved,
    updatedAt:  serverTimestamp(),
    approvedAt: approved ? serverTimestamp() : null,
  });
};

export const updateJob = async (jobId, fields) => {
  const ref = doc(db, "jobs", jobId);
  await updateDoc(ref, { ...fields, updatedAt: serverTimestamp() });
};

export const deleteJob = async (jobId) => deleteDoc(doc(db, "jobs", jobId));

// Bulk approve / delete using Firestore batched writes (max 499 ops per batch)
export const bulkApproveJobs = async (jobIds) => {
  const chunks = [];
  for (let i = 0; i < jobIds.length; i += 499) chunks.push(jobIds.slice(i, i + 499));
  for (const chunk of chunks) {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);
    for (const id of chunk) {
      batch.update(doc(db, "jobs", id), {
        approved: true, updatedAt: serverTimestamp(), approvedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
};

export const bulkDeleteJobs = async (jobIds) => {
  const chunks = [];
  for (let i = 0; i < jobIds.length; i += 499) chunks.push(jobIds.slice(i, i + 499));
  for (const chunk of chunks) {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);
    for (const id of chunk) batch.delete(doc(db, "jobs", id));
    await batch.commit();
  }
};

export const saveJob = async (jobData) => {
  const ref = doc(db, "jobs", jobData.id);
  await setDoc(ref, {
    ...jobData,
    approved:  false,
    scrapedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ── Admin: create a manual job posting ────────────────────────────────────────
// Uses addDoc so Firestore generates the ID — no documentId() misuse.
export const createManualJob = async (jobData) => {
  const payload = {
    ...jobData,
    source:     "Manual",
    manual:     true,
    approved:   true,
    datePosted: serverTimestamp(),
    createdAt:  serverTimestamp(),
    updatedAt:  serverTimestamp(),
  };

  // addDoc auto-generates a Firestore document ID and returns a reference
  const ref = await addDoc(collection(db, "jobs"), payload);

  // Return the job with the real Firestore ID set
  return { id: ref.id, ...payload };
};

// ── Credits ───────────────────────────────────────────────────────────────────

export const getUserCredits = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data().credits ?? 0) : 0;
};

// Admin: adjust credits for any user
export const adminGrantCredits = async (uid, amount, adminUid) => {
  await runTransaction(db, async (tx) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await tx.get(userRef);
    if (!userSnap.exists()) throw new Error("User not found");
    tx.update(userRef, {
      credits:   increment(amount),
      updatedAt: serverTimestamp(),
    });
    tx.set(doc(collection(db, "transactions")), {
      uid,
      adminUid,
      type:        "admin_grant",
      amount,
      description: `Admin granted ${amount} credit${Math.abs(amount) !== 1 ? "s" : ""}`,
      createdAt:   serverTimestamp(),
    });
  });
};

// Unlock a job (deduct 1 credit, record unlock + transaction)
export const unlockJob = async (uid, job) => {
  const unlockRef = doc(db, "jobUnlocks", `${uid}_${job.id}`);
  const userRef   = doc(db, "users", uid);

  return runTransaction(db, async (tx) => {
    const [unlockSnap, userSnap] = await Promise.all([
      tx.get(unlockRef),
      tx.get(userRef),
    ]);

    // Already unlocked — return without charging
    if (unlockSnap.exists()) return { alreadyUnlocked: true };

    const credits = userSnap.data()?.credits ?? 0;
    if (credits < 2) throw new Error("Insufficient credits");

    tx.set(unlockRef, {
      uid,
      jobId:      job.id,
      jobTitle:   job.title,
      source:     job.source,
      unlockedAt: serverTimestamp(),
    });
    tx.update(userRef, {
      credits:   increment(-2),
      updatedAt: serverTimestamp(),
    });
    tx.set(doc(collection(db, "transactions")), {
      uid,
      type:        "unlock",
      amount:      -2,
      jobId:       job.id,
      jobTitle:    job.title,
      description: `Unlocked: ${job.title}`,
      createdAt:   serverTimestamp(),
    });

    return { alreadyUnlocked: false };
  });
};

// Check if a user has already unlocked a specific job
export const checkJobUnlocked = async (uid, jobId) => {
  const snap = await getDoc(doc(db, "jobUnlocks", `${uid}_${jobId}`));
  return snap.exists();
};

// Get all jobs unlocked by a user
export const getUnlockedJobIds = async (uid) => {
  const q    = query(collection(db, "jobUnlocks"), where("uid", "==", uid));
  const snap = await getDocs(q);
  return new Set(snap.docs.map(d => d.data().jobId));
};

// Get all jobs saved/bookmarked by a user
export const getSavedJobIds = async (uid) => {
  const ref  = doc(db, "users", uid);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  return new Set(data.savedJobs || []);
};

// Toggle save/unsave a job (stored as array on user doc)
export const toggleSaveJob = async (uid, jobId) => {
  const ref  = doc(db, "users", uid);
  const snap = await getDoc(ref);
  const saved = snap.exists() ? (snap.data().savedJobs || []) : [];
  const alreadySaved = saved.includes(jobId);
  await updateDoc(ref, {
    savedJobs: alreadySaved
      ? saved.filter(id => id !== jobId)
      : [...saved, jobId],
  });
  return !alreadySaved; // returns true if now saved
};

// Get transaction history for a user
export const getUserTransactions = async (uid, maxItems = 20) => {
  const q    = query(
    collection(db, "transactions"),
    where("uid", "==", uid),
    orderBy("createdAt", "desc"),
    limit(maxItems)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
